export type SelectionToolbarContext = {
  selectedText: string;
  x: number;
  y: number;
};

function doesRangeIntersectContainer({
  range,
  container,
}: {
  range: Range;
  container: HTMLElement;
}): boolean {
  try {
    return range.intersectsNode(container);
  } catch {
    return false;
  }
}

export function getSelectedTextWithin(container: HTMLElement | null): string {
  if (!container || typeof window === 'undefined') {
    return '';
  }
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return '';
  }
  const selectedText = selection.toString().trim();
  if (!selectedText) {
    return '';
  }
  const anchorNode = selection.anchorNode;
  const focusNode = selection.focusNode;
  const range = selection.getRangeAt(0);
  const isSelectionInsideContainer =
    Boolean(anchorNode && container.contains(anchorNode)) ||
    Boolean(focusNode && container.contains(focusNode)) ||
    doesRangeIntersectContainer({ range, container });
  if (!isSelectionInsideContainer) {
    return '';
  }
  return selectedText;
}

function getSelectionFocusRect({
  selection,
  container,
}: {
  selection: Selection;
  container: HTMLElement;
}): DOMRect | null {
  const focusNode = selection.focusNode;
  if (!focusNode || !container.contains(focusNode)) {
    return null;
  }
  const focusRange = document.createRange();
  try {
    focusRange.setStart(focusNode, selection.focusOffset);
    focusRange.collapse(true);
    const focusRect = focusRange.getBoundingClientRect();
    if (focusRect.width > 0 || focusRect.height > 0) {
      return focusRect;
    }
  } catch {
    return null;
  }
  const rects = Array.from(selection.getRangeAt(0).getClientRects()).filter(
    (rect) => rect.width > 0 && rect.height > 0,
  );
  return rects[rects.length - 1] ?? null;
}

export function getSelectionToolbarContext({
  container,
  fallbackPosition,
}: {
  container: HTMLElement | null;
  fallbackPosition?: { x: number; y: number };
}): SelectionToolbarContext | null {
  if (!container || typeof window === 'undefined') {
    return null;
  }
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return null;
  }
  const selectedText = getSelectedTextWithin(container);
  if (!selectedText) {
    return null;
  }
  const range = selection.getRangeAt(0);
  const focusRect = getSelectionFocusRect({ selection, container });
  const rects = Array.from(range.getClientRects()).filter(
    (rect) => rect.width > 0 && rect.height > 0,
  );
  const anchorRect =
    focusRect ?? rects[rects.length - 1] ?? range.getBoundingClientRect();
  if (
    (!anchorRect || (anchorRect.width === 0 && anchorRect.height === 0)) &&
    !fallbackPosition
  ) {
    return null;
  }

  const buttonSize = 28;
  const gap = 6;
  const anchorX = fallbackPosition?.x ?? anchorRect.right;
  const anchorY = fallbackPosition?.y ?? anchorRect.top + anchorRect.height / 2;
  const preferredX = anchorX + gap;
  const fallbackX = anchorX - buttonSize - gap;
  const x = Math.min(
    Math.max(
      gap,
      preferredX > window.innerWidth - buttonSize - gap
        ? fallbackX
        : preferredX,
    ),
    window.innerWidth - buttonSize - gap,
  );
  const preferredY = anchorY - buttonSize / 2;
  const y = Math.min(
    Math.max(gap, preferredY),
    window.innerHeight - buttonSize - gap,
  );

  return { selectedText, x, y };
}

export function computeLineRange({
  fullText,
  excerpt,
}: {
  fullText: string;
  excerpt: string;
}): { lineStart: number; lineEnd: number } | undefined {
  const trimmedExcerpt = excerpt.trim();
  if (!trimmedExcerpt) {
    return undefined;
  }
  const selectionIndex = fullText.indexOf(trimmedExcerpt);
  if (selectionIndex < 0) {
    return undefined;
  }
  const beforeSelection = fullText.slice(0, selectionIndex);
  const lineStart = beforeSelection.split('\n').length;
  const lineEnd = lineStart + trimmedExcerpt.split('\n').length - 1;
  return { lineStart, lineEnd };
}
